import React, { useState } from 'react';

import { BILLING_FREQUENCY_OPTIONS } from '../../constants/BillingFrequencyConstants';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import { MemberFeeDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import Form from '../common/Form';
import FormField from '../common/FormField';

interface MemberFeeFormProps {
  initialValues: MemberFeeDTO;
  memberTypeId: string;
  onSubmit: (data: MemberFeeDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const validationRules: ValidationRules = {
  amount: [{ required: true, message: 'Amount is required' }],
  currency: [{ required: true, message: 'Currency is required' }],
  billingFrequencyId: [
    { required: true, message: 'Billing frequency is required' },
  ],
};

const MemberFeeForm: React.FC<MemberFeeFormProps> = ({
  initialValues,
  memberTypeId,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<MemberFeeDTO>({
    currency: 'USD',
    isActive: true,
    ...initialValues,
    memberTypeId,
  });

  const { errors, validate } = useFormValidation<MemberFeeDTO>(validationRules);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit({ ...formData, memberTypeId });
    }
  };

  return (
    <Form
      sections={[]}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isLoading}
      hideHeader
    >
      <FormField
        id="amount"
        label="Amount"
        type="decimal"
        value={formData.amount ?? ''}
        onChange={(e) => handleChange(e, setFormData)}
        placeholder="Enter Amount"
        required
        decimalPlaces={2}
        min={0}
        error={errors.amount}
      />
      <FormField
        id="currency"
        label="Currency"
        type="text"
        value={formData.currency || ''}
        onChange={(e) => handleChange(e, setFormData)}
        placeholder="e.g. USD"
        maxLength={10}
        required
        error={errors.currency}
      />
      <FormField
        id="billingFrequencyId"
        label="Billing Frequency"
        type="dropdown"
        value={formData.billingFrequencyId || ''}
        onChange={(e) => handleChange(e, setFormData)}
        options={BILLING_FREQUENCY_OPTIONS}
        placeholder="Select a Billing Frequency"
        required
        error={errors.billingFrequencyId}
      />
      <FormField
        id="stripePriceId"
        label="Stripe Price Id"
        type="text"
        value={formData.stripePriceId || ''}
        onChange={(e) => handleChange(e, setFormData)}
        placeholder="price_..."
        error={errors.stripePriceId}
      />
      <FormField
        id="stripeProductId"
        label="Stripe Product Id"
        type="text"
        value={formData.stripeProductId || ''}
        onChange={(e) => handleChange(e, setFormData)}
        placeholder="prod_..."
        error={errors.stripeProductId}
      />
      <FormField
        id="isActive"
        label="Is Active"
        type="checkbox"
        value={formData.isActive ?? true}
        onChange={(e) => handleChange(e, setFormData)}
        error={errors.isActive}
      />
    </Form>
  );
};

export default MemberFeeForm;
